import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, DatabaseBackup, Upload, Download, AlertTriangle, CheckCircle2, Mail, RefreshCw, UploadCloud } from "lucide-react";
import PausaTareas from "./PausaTareas"; // PAUSA-NEW: solo se dibuja si el backend tiene encendido el restaurador
import RestauracionBson from "./RestauracionBson"; // PROMPT 158: solo se dibuja si el backend lo tiene encendido

function fechaTag() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

export default function BackupConfig() {
  const [exportando, setExportando] = useState(false);
  const [exportandoCorreos, setExportandoCorreos] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const fileRef = useRef(null);

  // PROMPT 121: respaldo automatico de todas las noches en Cloudflare R2.
  const [auto, setAuto] = useState(null);
  const [lanzando, setLanzando] = useState(false);
  const cargarAuto = useCallback(async () => {
    try {
      const { data } = await api.get("/backup/automatico");
      setAuto(data);
    } catch (e) {
      setAuto(null);
    }
  }, []);
  useEffect(() => {
    cargarAuto();
  }, [cargarAuto]);
  const respaldarAhora = async () => {
    setLanzando(true);
    try {
      await api.post("/backup/automatico/ahora");
      toast.success("Respaldo en marcha. Tarda unos minutos: tocá Actualizar para ver cómo terminó.");
      setTimeout(cargarAuto, 3000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo iniciar el respaldo.");
    } finally {
      setLanzando(false);
    }
  };
  const fechaHora = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };
  const megas = (b) => (typeof b === "number" ? `${(b / 1024 / 1024).toFixed(1)} MB` : "—");
  const ESTADO_RESPALDO = {
    ok: { label: "OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    error: { label: "Falló", cls: "bg-red-50 text-red-700 border-red-200" },
    en_curso: { label: "En curso", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tours_ny_backup_${fechaTag()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Base de datos exportada. Revisá el archivo descargado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo exportar la base de datos.");
    } finally {
      setExportando(false);
    }
  };

  const exportarCorreosCrudos = async () => {
    setExportandoCorreos(true);
    try {
      // 1) pedimos el resumen para saber en cuantos lotes se parte
      const { data: info } = await api.get("/backup/export/emails/info", {
        params: { por_lote: 500 },
      });
      const totalLotes = info?.total_lotes || 0;
      const total = info?.total_documentos || 0;
      if (!total) {
        toast.info("No hay correos crudos para exportar en esta cuenta.");
        return;
      }
      toast.message(`Exportando correos crudos`, {
        description: `${total} correos en ${totalLotes} archivo(s). Se descargarán uno por uno.`,
      });
      // 2) bajamos cada lote como archivo aparte
      const tag = fechaTag();
      for (let pagina = 1; pagina <= totalLotes; pagina += 1) {
        const res = await api.get("/backup/export/emails", {
          params: { pagina, por_lote: info.por_lote },
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        const nnn = String(pagina).padStart(3, "0");
        const mmm = String(totalLotes).padStart(3, "0");
        a.download = `tours_ny_emails_crudos_${tag}_lote_${nnn}_de_${mmm}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
      toast.success(
        `Correos crudos exportados: ${total} correo(s) en ${totalLotes} archivo(s). ` +
        `Cada archivo se puede re-importar tal cual desde "Importar base de datos".`,
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo exportar los correos crudos.");
    } finally {
      setExportandoCorreos(false);
    }
  };

  const elegirArchivo = async (file) => {
    if (!file) return;
    setArchivo(file);
    setResultado(null);
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      const { data } = await api.post("/backup/import/preview", fd);
      setPreview(data);
      setDialogOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo leer el archivo.");
      setArchivo(null);
    } finally {
      setPreviewLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmarImport = async () => {
    if (!archivo) return;
    setAplicando(true);
    try {
      const fd = new FormData();
      fd.append("archivo", archivo);
      const { data } = await api.post("/backup/import/apply", fd);
      setResultado(data);
      setDialogOpen(false);
      toast.success(`Import completo: ${data.total_importado} documentos procesados.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo importar el archivo.");
    } finally {
      setAplicando(false);
      setArchivo(null);
    }
  };

  const colecciones = preview ? Object.keys(preview.resumen).sort() : [];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="backup-config">
      {/* PROMPT 121: respaldo automatico de todas las noches en Cloudflare R2. */}
      <Card className="p-6 space-y-4" data-testid="respaldo-automatico">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
            <UploadCloud className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Respaldo automático</h3>
            <p className="text-sm text-slate-500 mt-1">
              Todas las noches a las {auto?.hora || "03:00"} (hora de Nueva York) la plataforma guarda una copia de toda
              la base en Cloudflare R2, en una cuenta que es solo de Interviajes: un archivo con todo, incluido el
              historial, y otro listo para subir tal cual en «Importar base de datos». Se guardan los de cada día
              durante 30 días, el del domingo durante 6 meses y el del día 1 de cada mes durante 2 años. Nunca
              incluye claves ni contraseñas.
            </p>
          </div>
        </div>
        {auto && !auto.configurado && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800 text-sm" data-testid="respaldo-sin-configurar">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Todavía no está conectado a Cloudflare R2. Faltan estas variables en el servidor:{" "}
              {(auto.faltan || []).join(", ")}.
            </span>
          </div>
        )}
        {auto?.ultimos?.length ? (
          <div className="border border-slate-100 rounded-md overflow-x-auto">
            <table className="w-full text-xs" data-testid="respaldo-ultimos">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium text-slate-500">Cuándo</th>
                  <th className="text-left px-3 py-1.5 font-medium text-slate-500">Tipo</th>
                  <th className="text-left px-3 py-1.5 font-medium text-slate-500">Estado</th>
                  <th className="text-right px-3 py-1.5 font-medium text-slate-500">Tamaño</th>
                </tr>
              </thead>
              <tbody>
                {auto.ultimos.map((r, i) => {
                  const e = ESTADO_RESPALDO[r.estado] || ESTADO_RESPALDO.en_curso;
                  return (
                    <tr key={`${r.inicio}-${i}`} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{fechaHora(r.inicio)}</td>
                      <td className="px-3 py-1.5 text-slate-600">{r.motivo === "automatico" ? "De la noche" : "A pedido"}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${e.cls}`}>{e.label}</span>
                        {r.error ? <p className="text-[11px] text-red-600 mt-1">{r.error}</p> : null}
                      </td>
                      <td className="px-3 py-1.5 text-right text-slate-700 whitespace-nowrap">{megas(r.bytes_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : auto?.configurado ? (
          <p className="text-sm text-slate-500" data-testid="respaldo-sin-ninguno">Todavía no se hizo ningún respaldo.</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={respaldarAhora}
            disabled={!auto?.configurado || auto?.en_curso || lanzando}
            data-testid="respaldar-ahora-btn"
            className="gap-2"
          >
            {lanzando ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            Respaldar ahora
          </Button>
          <Button variant="outline" onClick={cargarAuto} data-testid="respaldo-actualizar-btn" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Actualizar
          </Button>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
            <DatabaseBackup className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Exportar base de datos</h3>
            <p className="text-sm text-slate-500 mt-1">
              Descarga un único archivo JSON para migrar de cuenta: reservas, tours, salidas, agencias,
              usuarios, tarifas, plantillas, reglas, marcas, buzones, cuentas y mapeos de Bókun, alertas,
              solicitudes de cambio y respuestas. Preserva los IDs originales para no romper las relaciones.
              No incluye claves ni contraseñas de APIs (Bókun, correo): esas se cargan a mano en la cuenta
              nueva vía variables de entorno. Para que el archivo sea liviano, tampoco incluye el log de
              auditoría ni los correos crudos (son historial, no dato operativo — las reservas ya quedan con
              los datos parseados). Si necesitás conservar los correos crudos, se pueden bajar aparte y
              paginados en varios archivos con el botón de abajo (se re-importan tal cual desde el mismo
              «Importar base de datos», sin duplicar por <code>message_id</code>).
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={exportar} disabled={exportando} data-testid="exportar-base-datos-btn" className="gap-2">
            {exportando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exportar base de datos
          </Button>
          <Button
            onClick={exportarCorreosCrudos}
            disabled={exportandoCorreos}
            variant="outline"
            data-testid="exportar-correos-crudos-btn"
            className="gap-2"
          >
            {exportandoCorreos ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Exportar correos (por lotes)
          </Button>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
            <Upload className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Importar base de datos</h3>
            <p className="text-sm text-slate-500 mt-1">
              Subí el archivo JSON exportado desde la cuenta anterior, o tal cual el .json.gz «para importar» del
              respaldo automático. Primero se muestra una vista previa con
              el conteo de lo que trae el archivo vs. lo que ya existe en esta base, antes de confirmar. Es
              seguro re-ejecutarlo: no duplica registros (respeta los IDs originales).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".json,.gz,application/json,application/gzip"
            onChange={(e) => elegirArchivo(e.target.files?.[0])}
            disabled={previewLoading}
            data-testid="importar-base-datos-input"
            className="text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800"
          />
          {previewLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>

        {resultado && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 space-y-1" data-testid="import-resultado">
            <p className="text-sm text-emerald-800 flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4" /> Import aplicado: {resultado.total_importado} documentos.
            </p>
            <div className="text-xs text-emerald-700 grid grid-cols-2 sm:grid-cols-3 gap-x-4">
              {Object.entries(resultado.totales_en_destino_ahora).map(([coll, n]) => (
                <span key={coll}>{coll}: {n}</span>
              ))}
            </div>
          </div>
        )}
      </Card>

      <AlertDialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setArchivo(null); } }}>
        <AlertDialogContent data-testid="import-preview-dialog" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Vista previa de la importación</AlertDialogTitle>
            <AlertDialogDescription>
              Revisá el conteo por colección antes de confirmar. El import respeta los IDs originales: no
              duplica nada si lo corrés más de una vez.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            {preview?.destino_tiene_datos && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800 text-sm" data-testid="import-aviso-datos-existentes">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Esta base ya tiene datos ({preview.total_actual_en_destino} documentos). Los registros
                  con el mismo ID se sobrescribirán; el resto se agrega. No se duplica nada.
                </span>
              </div>
            )}
            <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium text-slate-500">Colección</th>
                    <th className="text-right px-3 py-1.5 font-medium text-slate-500">En archivo</th>
                    <th className="text-right px-3 py-1.5 font-medium text-slate-500">Ya en destino</th>
                  </tr>
                </thead>
                <tbody>
                  {colecciones.map((coll) => (
                    <tr key={coll} className="border-t border-slate-100" data-testid={`preview-row-${coll}`}>
                      <td className="px-3 py-1 text-slate-700">{coll}</td>
                      <td className="px-3 py-1 text-right text-slate-900">{preview.resumen[coll].en_archivo}</td>
                      <td className="px-3 py-1 text-right text-slate-500">{preview.resumen[coll].actual_en_destino}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">
              Total en archivo: <span className="font-medium text-slate-800">{preview?.total_en_archivo}</span>
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancelar-import-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarImport}
              disabled={aplicando}
              data-testid="confirmar-import-btn"
              className="gap-2"
            >
              {aplicando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Sí, importar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PausaTareas />
      <RestauracionBson />
    </div>
  );
}
